import { validateApplicationRuntimeCapabilities } from "@cogo/types-v2"
import designRelease from "../composition/fixtures/mobile-core.release.json"
import type { DesignCompositionRelease } from "../composition/types"
import { compileDesignBoundApplication } from "../design-ontology-binding/compile-design-bound-application"
import applicationBlueprint from "./fixtures/mobile-field-task/mobile-field-task.application-blueprint.json"
import fieldTaskCard from "./fixtures/mobile-field-task/field-task-card.ux-pattern.json"
import fieldTaskConfirmation from "./fixtures/mobile-field-task/field-task-confirmation.ux-pattern.json"
import fieldTaskDetail from "./fixtures/mobile-field-task/field-task-detail.ux-pattern.json"
import fieldTaskExecution from "./fixtures/mobile-field-task/field-task-execution.ux-pattern.json"
import fieldTaskRecovery from "./fixtures/mobile-field-task/field-task-recovery.ux-pattern.json"
import { compileLibraryComposition } from "./compile-library-composition"
import type {
  ApplicationBlueprint,
  ApplicationCapabilityRequirement,
  ApplicationWebMcpMode,
  LibraryAuthorityContext,
  LibraryCompositionArtifact,
  LibraryCompositionInput,
  LibraryCompileResult,
  LibraryGap,
  UxPatternDefinition,
} from "./types"

export const MOBILE_FIELD_TASK_LIBRARY_AUTHORITY = {
  admittedUxResearchRefs: ["cogo:ux-observation/field-task-card-v1", "cogo:ux-observation/field-task-detail-v1", "cogo:ux-observation/field-task-recovery-v1"],
  approvedCanonicalSourceRefs: ["cogo:approved-source/field-task-confirmation-v1", "cogo:approved-source/field-task-execution-v1"],
  admittedApplicationCoreRefs: ["cogo:application-core/mobile-field-task-v1"],
} satisfies LibraryAuthorityContext

const MOBILE_FIELD_TASK_APPLICATION_NAMESPACE =
  "cogo://application-blueprint/mobile-field-task/field-run-reference"
const MOBILE_FIELD_TASK_A_NAMESPACE =
  `${MOBILE_FIELD_TASK_APPLICATION_NAMESPACE}/ux/task-a`
const MOBILE_FIELD_TASK_B_NAMESPACE =
  `${MOBILE_FIELD_TASK_APPLICATION_NAMESPACE}/ux/task-b`

const MOBILE_FIELD_TASK_UX_PATTERNS = [
  fieldTaskCard,
  fieldTaskDetail,
  fieldTaskExecution,
  fieldTaskConfirmation,
  fieldTaskRecovery,
]

export const MOBILE_FIELD_TASK_CAPABILITY_REQUEST_HASH =
  `sha256:${"0".repeat(64)}` as const

/**
 * The reference application declares `disabled` explicitly, so the reference
 * requirement carries the same mode and an empty evidence list.
 */
export function mobileFieldTaskCapabilityRequirement(): ApplicationCapabilityRequirement {
  return {
    schemaVersion: "cogo.application-capability-requirement.v1",
    requestedWebMcpMode:
      applicationBlueprint.runtimeCapabilities.webMcp
        .mode as ApplicationWebMcpMode,
    requestHash: MOBILE_FIELD_TASK_CAPABILITY_REQUEST_HASH,
    requirementEvidenceRefs: [],
  }
}

export function mobileFieldTaskLibraryCandidate(
  applicationInstanceId: string,
): LibraryCompositionInput {
  return {
    applicationInstanceId,
    applicationBlueprint: applicationBlueprint as ApplicationBlueprint,
    uxPatterns: MOBILE_FIELD_TASK_UX_PATTERNS as UxPatternDefinition[],
    capabilityRequirement: mobileFieldTaskCapabilityRequirement(),
  }
}

function exactMobileFieldTaskSourceRefs(): string[] {
  const definitions = new Map(
    MOBILE_FIELD_TASK_UX_PATTERNS.map((definition) => [
      `${definition.uxPatternId}@${definition.version}`,
      definition,
    ]),
  )
  return [
    ...new Set([
      ...applicationBlueprint.lineage.sourceRefs,
      ...[
        ...new Set(
          applicationBlueprint.uxInstances.map(
            (instance) => instance.definitionRef,
          ),
        ),
      ].flatMap(
        (definitionRef) =>
          definitions.get(definitionRef)?.lineage.sourceRefs ?? [],
      ),
    ]),
  ].sort()
}

export function compileMobileFieldTaskLibraryReference(
  applicationInstanceId = "field-run-reference",
): LibraryCompileResult {
  return compileLibraryComposition(
    mobileFieldTaskLibraryCandidate(applicationInstanceId),
    MOBILE_FIELD_TASK_LIBRARY_AUTHORITY,
  )
}

export function verifyMobileFieldTaskLibraryReference(): {
  ok: boolean
  gaps: LibraryGap[]
  artifact?: LibraryCompositionArtifact
} {
  const result = compileMobileFieldTaskLibraryReference()
  return result.ok
    ? { ok: true, gaps: [], artifact: result.artifact }
    : { ok: false, gaps: result.gaps }
}

export function evaluateMobileFieldTaskLineage({ sourceRefs, legacyRefs, applicationCoreRef }: {
  sourceRefs: string[]
  legacyRefs: string[]
  applicationCoreRef: string
}) {
  const refs = [...legacyRefs].sort()
  const admittedRefs = new Set([
    ...MOBILE_FIELD_TASK_LIBRARY_AUTHORITY.admittedUxResearchRefs,
    ...MOBILE_FIELD_TASK_LIBRARY_AUTHORITY.approvedCanonicalSourceRefs,
    ...MOBILE_FIELD_TASK_LIBRARY_AUTHORITY.admittedApplicationCoreRefs,
  ])
  const unapprovedRefs = sourceRefs.filter((ref) => !admittedRefs.has(ref)).sort()
  const exactSourceRefs = exactMobileFieldTaskSourceRefs()
  const sourceRefsAreExact =
    sourceRefs.length > 0 &&
    sourceRefs.length === exactSourceRefs.length &&
    sourceRefs.every((ref, index) => ref === exactSourceRefs[index])
  const coreIsExact =
    applicationCoreRef === applicationBlueprint.applicationCoreRef
  return {
    status:
      refs.length === 0 &&
      unapprovedRefs.length === 0 &&
      sourceRefsAreExact &&
      coreIsExact
        ? ("pass" as const)
        : ("fail" as const),
    refs,
    unapprovedRefs,
  }
}

export async function runMobileFieldTaskLibraryProof() {
  const candidate = mobileFieldTaskLibraryCandidate("field-run-reference")
  const libraryResult = compileLibraryComposition(candidate, MOBILE_FIELD_TASK_LIBRARY_AUTHORITY)
  const unadmittedCandidate = structuredClone(candidate)
  unadmittedCandidate.uxPatterns[0].lineage.sourceRefs = ["cogo:ux-observation/not-admitted-anywhere"]
  const unadmittedResult = compileLibraryComposition(unadmittedCandidate, MOBILE_FIELD_TASK_LIBRARY_AUTHORITY)
  const foundationResult = await compileDesignBoundApplication(
    {
      library: candidate,
      libraryAuthority: MOBILE_FIELD_TASK_LIBRARY_AUTHORITY,
      designOntologyReleaseRef: designRelease.releaseId,
    },
    {
      resolveDesignOntologyRelease: async (releaseRef) =>
        releaseRef === designRelease.releaseId
          ? (structuredClone(designRelease) as DesignCompositionRelease)
          : null,
    },
  )
  const repeatedInstanceIsolation =
    evaluateMobileFieldTaskRepeatedInstanceIsolation(libraryResult)
  const lineage = evaluateMobileFieldTaskLineage({
    sourceRefs: libraryResult.ok ? libraryResult.artifact.provenance.sourceRefs : [],
    legacyRefs: libraryResult.ok ? libraryResult.artifact.provenance.legacyRefs : [],
    applicationCoreRef: libraryResult.ok
      ? libraryResult.artifact.provenance.applicationCoreRef
      : "",
  })
  const selections = repeatedSelectionEntries(
    MOBILE_FIELD_TASK_APPLICATION_NAMESPACE,
    MOBILE_FIELD_TASK_A_NAMESPACE,
    MOBILE_FIELD_TASK_B_NAMESPACE,
  )
  const repeatedSelectionEntryWiring = libraryResult.ok
    ? { status: selectionWiringIsExact(libraryResult.artifact, selections) ? ("pass" as const) : ("fail" as const), selections }
    : { status: "fail" as const, selections }
  return {
    libraryCompilation: { status: libraryResult.ok ? ("pass" as const) : ("fail" as const) },
    foundationCompilation: { status: foundationResult.ok ? ("pass" as const) : ("fail" as const) },
    repeatedInstanceIsolation,
    repeatedSelectionEntryWiring,
    applicationCapability:
      evaluateMobileFieldTaskApplicationCapability(libraryResult),
    unadmittedSourceRegression: { status: !unadmittedResult.ok && unadmittedResult.gaps.some((gap) => gap.code === "ux-pattern-not-admitted") ? ("pass" as const) : ("fail" as const) },
    legacyLineage: { status: libraryResult.ok && lineage.status === "pass" ? ("pass" as const) : ("fail" as const), refs: lineage.refs, unapprovedRefs: lineage.unapprovedRefs },
    renderEvidence: { status: "not-run" as const },
    accessibilityEvidence: { status: "not-run" as const },
    humanVisualApproval: { status: "not-run" as const },
    promotion: { status: "blocked" as const, reasons: ["render-evidence-missing", "accessibility-evidence-missing", "human-visual-approval-missing"] },
  }
}

/**
 * Prove the compiled capability profile against the shared runtime contract:
 * the compiled mode equals the Blueprint declaration, the manifest exists only
 * for enabled modes, and the profile/manifest pair is one the Web runtime is
 * allowed to install.
 */
export function evaluateMobileFieldTaskApplicationCapability(
  libraryResult: LibraryCompileResult,
) {
  if (!libraryResult.ok) {
    return { status: "fail" as const, mode: null, manifestPresent: false }
  }
  const declaredMode = applicationBlueprint.runtimeCapabilities.webMcp.mode
  const compiled = libraryResult.artifact.runtimeCapabilities
  const manifestPresent =
    "applicationWebMcpManifest" in libraryResult.artifact
  const admitted = validateApplicationRuntimeCapabilities(
    compiled,
    libraryResult.artifact.applicationWebMcpManifest,
  )
  return {
    status:
      admitted.ok &&
      compiled.webMcp.mode === declaredMode &&
      manifestPresent === (declaredMode !== "disabled")
        ? ("pass" as const)
        : ("fail" as const),
    mode: compiled.webMcp.mode,
    manifestPresent,
  }
}

export function evaluateMobileFieldTaskRepeatedInstanceIsolation(
  libraryResult: LibraryCompileResult,
) {
  if (!libraryResult.ok) {
    return {
      status: "fail" as const,
      taskAResources: emptyInstanceResources(),
      taskBResources: emptyInstanceResources(),
    }
  }

  const taskAEntries = libraryResult.artifact.scopeManifest.filter(
    (entry) =>
      entry.scopeKind === "ux-instance" &&
      entry.namespaceId === MOBILE_FIELD_TASK_A_NAMESPACE,
  )
  const taskBEntries = libraryResult.artifact.scopeManifest.filter(
    (entry) =>
      entry.scopeKind === "ux-instance" &&
      entry.namespaceId === MOBILE_FIELD_TASK_B_NAMESPACE,
  )
  const taskAResources = exactInstanceResources(
    libraryResult.artifact,
    MOBILE_FIELD_TASK_A_NAMESPACE,
  )
  const taskBResources = exactInstanceResources(
    libraryResult.artifact,
    MOBILE_FIELD_TASK_B_NAMESPACE,
  )
  const expectedTaskAResources = expectedInstanceResources(
    MOBILE_FIELD_TASK_A_NAMESPACE,
  )
  const expectedTaskBResources = expectedInstanceResources(
    MOBILE_FIELD_TASK_B_NAMESPACE,
  )

  return {
    status:
      taskAEntries.length === 1 &&
      taskBEntries.length === 1 &&
      exactResourceSetsMatch(taskAResources, expectedTaskAResources) &&
      exactResourceSetsMatch(taskBResources, expectedTaskBResources) &&
      exactResourceSetsAreDisjoint(taskAResources, taskBResources)
        ? ("pass" as const)
        : ("fail" as const),
    taskAResources,
    taskBResources,
  }
}

function repeatedSelectionEntries(appNamespace: string, taskAId: string, taskBId: string) {
  return [
    { sourcePortId: `${taskAId}#port/task-selected`, applicationPortId: `${appNamespace}#port/task-a-selected`, applicationEventId: `${appNamespace}#event/task-a-selected`, applicationFlowId: `${appNamespace}#flow/coordinate-task-a-selection`, nextJourneyStepId: `${appNamespace}#journey-step/review-task` },
    { sourcePortId: `${taskBId}#port/task-selected`, applicationPortId: `${appNamespace}#port/task-b-selected`, applicationEventId: `${appNamespace}#event/task-b-selected`, applicationFlowId: `${appNamespace}#flow/coordinate-task-b-selection`, nextJourneyStepId: `${appNamespace}#journey-step/review-task` },
  ]
}

function selectionWiringIsExact(artifact: LibraryCompositionArtifact, selections: ReturnType<typeof repeatedSelectionEntries>) {
  const entryStep = artifact.runtime.journey.steps.find((step) => step.stepId === artifact.runtime.journey.entryStepId)
  return selections.every((selection) =>
    artifact.runtime.portBindings.some((binding) => binding.sourcePortId === selection.sourcePortId && binding.targetPortId === selection.applicationPortId) &&
    artifact.runtime.applicationEventIds.includes(selection.applicationEventId) &&
    artifact.runtime.applicationFlows.some((flow) => flow.flowId === selection.applicationFlowId && flow.eventId === selection.applicationEventId) &&
    entryStep?.transitions.some((transition) => transition.eventId === selection.applicationEventId && transition.toStepId === selection.nextJourneyStepId && transition.path === "forward") === true,
  )
}

function exactInstanceResources(artifact: LibraryCompositionArtifact, namespaceId: string) {
  const statechart = safeArray(artifact.runtime.uxStatecharts).find(
    (candidate) => candidate.namespaceId === namespaceId,
  )
  const transitions = safeArray(statechart?.transitions)
  const semanticActions = safeArray(artifact.runtime.semanticActions)
  return {
    states: safeArray(statechart?.states).map((state) => state.stateId),
    events: [...safeArray(statechart?.eventIds)],
    actions: [...new Set([
      ...transitions.flatMap((transition) =>
        safeArray(transition.effectActionIds),
      ),
      ...semanticActions
        .filter((action) => action.uxInstanceNamespaceId === namespaceId)
        .map((action) => action.actionId),
    ])],
    results: safeArray(artifact.runtime.results)
      .filter((result) => result.ownerScopeId === namespaceId)
      .map((result) => result.resultId),
    pages: safeArray(artifact.runtime.pages)
      .filter((page) => page.uxInstanceNamespaceId === namespaceId)
      .map((page) => page.pageId),
    blocks: safeArray(artifact.runtime.blocks)
      .filter((block) => block.uxInstanceNamespaceId === namespaceId)
      .map((block) => block.blockId),
  }
}

function expectedInstanceResources(namespaceId: string) {
  return {
    states: [
      `${namespaceId}#state/idle`,
      `${namespaceId}#state/selected`,
    ],
    events: [
      `${namespaceId}#event/select-task`,
      `${namespaceId}#event/task-selected`,
    ],
    actions: [
      `${namespaceId}#action/emit-selection`,
      `${namespaceId}/page/choose-task/block/accept-task/action/semantic-action`,
    ],
    results: [`${namespaceId}#result/task-selected`],
    pages: [`${namespaceId}/page/choose-task`],
    blocks: [
      `${namespaceId}/page/choose-task/block/task-summary`,
      `${namespaceId}/page/choose-task/block/accept-task`,
    ],
  }
}

function emptyInstanceResources() {
  return { states: [] as string[], events: [] as string[], actions: [] as string[], results: [] as string[], pages: [] as string[], blocks: [] as string[] }
}

function safeArray<T>(value: readonly T[] | null | undefined): readonly T[] {
  return Array.isArray(value) ? value : []
}

function exactResourceSetsMatch(
  actual: ReturnType<typeof emptyInstanceResources>,
  expected: ReturnType<typeof emptyInstanceResources>,
) {
  return (Object.keys(expected) as Array<keyof typeof expected>).every(
    (category) =>
      expected[category].length > 0 &&
      actual[category].length === expected[category].length &&
      new Set(actual[category]).size === expected[category].length &&
      expected[category].every((resourceId) =>
        actual[category].includes(resourceId),
      ),
  )
}

function exactResourceSetsAreDisjoint(left: ReturnType<typeof emptyInstanceResources>, right: ReturnType<typeof emptyInstanceResources>) {
  return (Object.keys(left) as Array<keyof typeof left>).every((category) => new Set([...left[category], ...right[category]]).size === left[category].length + right[category].length)
}
